import { useMemo, useState } from "react";
import { ChevronDown, Minus, Plus } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EnumBadge } from "@/components/badges";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/utils";

const STATUS_FIELD_PRIORITY = [
  "success",
  "pa_status",
  "pa_status_obtained",
  "success_category",
  "partial_info_obtained",
  "coverage_info_obtained",
  "fail_reason",
  "pa_details",
  "fail_details",
  "missing_information",
] as const;

const DIFF_HIDDEN_KEY_PREFIXES = ["gemini_"] as const;
const DIFF_HIDDEN_KEYS = new Set(["structured_outputs_source"]);

type DiffKind = "changed" | "added" | "removed" | "same";

interface FieldDiff {
  key: string;
  kind: DiffKind;
  current: unknown;
  next: unknown;
}

function isHiddenDiffKey(key: string): boolean {
  if (DIFF_HIDDEN_KEYS.has(key)) return true;
  return DIFF_HIDDEN_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));
}

function valuesEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function formatFieldLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\bHCPCS\b/gi, "HCPCS")
    .replace(/\bCPT\b/gi, "CPT")
    .replace(/\bPA\b/gi, "PA")
    .replace(/\b([a-z])/g, (m) => m.toUpperCase());
}

function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function buildFieldDiffs(
  currentOutputs: Record<string, unknown>,
  newOutputs: Record<string, unknown>,
): FieldDiff[] {
  const keys = new Set<string>([
    ...Object.keys(currentOutputs),
    ...Object.keys(newOutputs),
  ]);

  const diffs: FieldDiff[] = [];
  for (const key of keys) {
    if (isHiddenDiffKey(key)) continue;
    const hasCurrent = key in currentOutputs;
    const hasNext = key in newOutputs;
    const current = currentOutputs[key];
    const next = newOutputs[key];

    let kind: DiffKind = "same";
    if (hasCurrent && hasNext) {
      kind = valuesEqual(current, next) ? "same" : "changed";
    } else if (hasNext) {
      kind = "added";
    } else {
      kind = "removed";
    }

    diffs.push({ key, kind, current, next });
  }

  const priorityIndex = new Map(
    STATUS_FIELD_PRIORITY.map((key, index) => [key, index]),
  );

  return diffs.sort((a, b) => {
    const kindOrder: Record<DiffKind, number> = {
      changed: 0,
      added: 1,
      removed: 2,
      same: 3,
    };
    const byKind = kindOrder[a.kind] - kindOrder[b.kind];
    if (byKind !== 0) return byKind;

    const aPriority = priorityIndex.get(a.key as (typeof STATUS_FIELD_PRIORITY)[number]);
    const bPriority = priorityIndex.get(b.key as (typeof STATUS_FIELD_PRIORITY)[number]);
    if (aPriority !== undefined || bPriority !== undefined) {
      if (aPriority === undefined) return 1;
      if (bPriority === undefined) return -1;
      return aPriority - bPriority;
    }
    return a.key.localeCompare(b.key);
  });
}

function StatusValue({
  fieldKey,
  value,
}: {
  fieldKey: string;
  value: unknown;
}) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-foreground">—</span>;
  }

  if (fieldKey === "pa_status" || fieldKey === "fail_reason" || fieldKey === "success_category") {
    return <EnumBadge value={String(value)} />;
  }

  if (fieldKey === "success" || fieldKey === "partial_info_obtained" || fieldKey === "pa_status_obtained") {
    const truthy = value === true || value === "true";
    return (
      <Badge
        className={cn(
          truthy
            ? "bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-950/50 dark:text-green-100"
            : "bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-950/50 dark:text-red-100",
        )}
      >
        {String(value)}
      </Badge>
    );
  }

  const text = formatFieldValue(value);
  const multiline = text.includes("\n") || text.length > 120;
  if (multiline) {
    return (
      <pre className="whitespace-pre-wrap break-words font-sans text-xs leading-relaxed">
        {text}
      </pre>
    );
  }
  return <span className="break-words text-xs leading-relaxed">{text}</span>;
}

function DiffCell({
  kind,
  side,
  fieldKey,
  value,
}: {
  kind: DiffKind;
  side: "current" | "next";
  fieldKey: string;
  value: unknown;
}) {
  const isCurrent = side === "current";
  const highlight =
    kind === "changed" ||
    (kind === "added" && !isCurrent) ||
    (kind === "removed" && isCurrent);

  return (
    <div
      className={cn(
        "min-h-[2.25rem] max-h-48 overflow-y-auto overflow-x-hidden rounded-md border px-2.5 py-2",
        highlight && kind === "changed" && isCurrent &&
          "border-red-300/70 bg-red-50/90 dark:border-red-900/60 dark:bg-red-950/30",
        highlight && kind === "changed" && !isCurrent &&
          "border-green-300/70 bg-green-50/90 dark:border-green-900/60 dark:bg-green-950/30",
        highlight && kind === "removed" && isCurrent &&
          "border-red-300/70 bg-red-50/90 dark:border-red-900/60 dark:bg-red-950/30",
        highlight && kind === "added" && !isCurrent &&
          "border-green-300/70 bg-green-50/90 dark:border-green-900/60 dark:bg-green-950/30",
        !highlight && "border-transparent bg-muted/20",
      )}
    >
      <StatusValue fieldKey={fieldKey} value={value} />
    </div>
  );
}

function FieldDiffRow({ diff }: { diff: FieldDiff }) {
  return (
    <div className="space-y-1.5 rounded-md border bg-background/60 p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium">{formatFieldLabel(diff.key)}</span>
        <code className="text-[10px] text-muted-foreground">{diff.key}</code>
        {diff.kind === "changed" ? (
          <Badge variant="outline" className="text-[10px]">
            changed
          </Badge>
        ) : null}
        {diff.kind === "added" ? (
          <Badge className="gap-0.5 bg-green-100 text-[10px] text-green-900 hover:bg-green-100 dark:bg-green-950/50 dark:text-green-100">
            <Plus className="h-3 w-3" />
            added
          </Badge>
        ) : null}
        {diff.kind === "removed" ? (
          <Badge className="gap-0.5 bg-red-100 text-[10px] text-red-900 hover:bg-red-100 dark:bg-red-950/50 dark:text-red-100">
            <Minus className="h-3 w-3" />
            removed
          </Badge>
        ) : null}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <DiffCell
          kind={diff.kind}
          side="current"
          fieldKey={diff.key}
          value={diff.current}
        />
        <DiffCell
          kind={diff.kind}
          side="next"
          fieldKey={diff.key}
          value={diff.next}
        />
      </div>
    </div>
  );
}

interface StructuredOutputDiffViewProps {
  currentOutputs: Record<string, unknown>;
  newOutputs: Record<string, unknown>;
}

export function StructuredOutputDiffView({
  currentOutputs,
  newOutputs,
}: StructuredOutputDiffViewProps) {
  const [unchangedOpen, setUnchangedOpen] = useState(false);
  const [rawOpen, setRawOpen] = useState(false);

  const diffs = useMemo(
    () => buildFieldDiffs(currentOutputs, newOutputs),
    [currentOutputs, newOutputs],
  );

  const changedDiffs = diffs.filter((d) => d.kind !== "same");
  const unchangedDiffs = diffs.filter((d) => d.kind === "same");

  if (diffs.length === 0) {
    return (
      <p className="text-sm italic text-muted-foreground">
        No structured output fields to compare
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-medium text-muted-foreground">Previous (stored)</span>
        <span className="text-muted-foreground">→</span>
        <span className="font-medium text-green-800 dark:text-green-200">
          New (regression)
        </span>
        {changedDiffs.length > 0 ? (
          <Badge className="bg-amber-100 text-[10px] text-amber-900 hover:bg-amber-100 dark:bg-amber-950/50 dark:text-amber-100">
            {changedDiffs.length} field{changedDiffs.length === 1 ? "" : "s"} changed
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px]">
            No field changes
          </Badge>
        )}
      </div>

      <div className="hidden gap-2 px-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground sm:grid sm:grid-cols-2">
        <span>Previous</span>
        <span>New</span>
      </div>

      <ScrollArea className="h-[32rem] pr-3">
        <div className="space-y-2">
          {changedDiffs.length > 0 ? (
            changedDiffs.map((diff) => <FieldDiffRow key={diff.key} diff={diff} />)
          ) : (
            <p className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
              Regression output matches stored structured output for all compared fields.
            </p>
          )}
        </div>
      </ScrollArea>

      {unchangedDiffs.length > 0 ? (
        <Collapsible open={unchangedOpen} onOpenChange={setUnchangedOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-1 text-xs">
              <ChevronDown
                className={cn(
                  "h-3 w-3 transition-transform",
                  unchangedOpen && "rotate-180",
                )}
              />
              {unchangedOpen ? "Hide" : "Show"} {unchangedDiffs.length} unchanged field
              {unchangedDiffs.length === 1 ? "" : "s"}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <ScrollArea className="mt-2 h-[24rem] pr-3">
              <div className="space-y-2">
                {unchangedDiffs.map((diff) => (
                  <FieldDiffRow key={diff.key} diff={diff} />
                ))}
              </div>
            </ScrollArea>
          </CollapsibleContent>
        </Collapsible>
      ) : null}

      <Collapsible open={rawOpen} onOpenChange={setRawOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1 text-xs">
            <ChevronDown
              className={cn("h-3 w-3 transition-transform", rawOpen && "rotate-180")}
            />
            {rawOpen ? "Hide" : "Show"} raw JSON
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <ScrollArea className="h-60 rounded-md border border-red-200/60 bg-red-50/40 dark:border-red-900/40 dark:bg-red-950/20">
              <pre className="p-3 text-[11px]">
                {JSON.stringify(currentOutputs, null, 2)}
              </pre>
            </ScrollArea>
            <ScrollArea className="h-60 rounded-md border border-green-200/60 bg-green-50/40 dark:border-green-900/40 dark:bg-green-950/20">
              <pre className="p-3 text-[11px]">
                {JSON.stringify(newOutputs, null, 2)}
              </pre>
            </ScrollArea>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export function structuredOutputFieldChangeCount(
  currentOutputs: Record<string, unknown>,
  newOutputs: Record<string, unknown>,
): number {
  return buildFieldDiffs(currentOutputs, newOutputs).filter((d) => d.kind !== "same").length;
}
